import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';
import { Shield, Truck, Award, Headphones, ChevronRight, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { QUOTE_INFO, Product } from '../data/products';
import { productsApi, transformProduct } from '../api/products';
import ProductCard from '../components/ProductCard';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function Home() {
  usePageTitle("Lodiss — Équipements de protection et vêtements professionnels");

  const { lang, setPage, setCatalogueFilter } = useApp();
  const T = t[lang];

  const [bestsellers, setBestsellers] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productsApi.getBestsellers(4)
      .then(data => setBestsellers(data.products.map(transformProduct)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const universeCards = [
    {
      key: 'vetements',
      icon: '👔',
      image: 'https://images.pexels.com/photos/8961065/pexels-photo-8961065.jpeg',
      data: T.universes.vetements,
      filter: 'vetements',
    },
    {
      key: 'epi',
      icon: '🦺',
      image: 'https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg',
      data: T.universes.epi,
      filter: 'epi',
    },
    {
      key: 'batteries',
      icon: '🔋',
      image: 'https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg',
      data: T.universes.batteries,
      filter: 'batteries',
    },
  ];

  const trustItems = [
    { icon: <Shield size={20} />, label: T.trust.secure },
    { icon: <Truck size={20} />, label: T.trust.delivery },
    { icon: <Award size={20} />, label: T.trust.norms },
    { icon: <Headphones size={20} />, label: T.trust.support },
  ];

  const thresholdRows = [
    { cat: T.howToBuy.thresholds.vetements, val: `≥ ${QUOTE_INFO.vetements.threshold} ${lang === 'fr' ? 'unités' : 'units'}` },
    { cat: T.howToBuy.thresholds.epi, val: `≥ ${QUOTE_INFO.epi.threshold} ${lang === 'fr' ? 'unités' : 'units'}` },
    { cat: T.howToBuy.thresholds.batteries, val: `≥ ${QUOTE_INFO.batteries.threshold} ${lang === 'fr' ? 'unités' : 'units'}` },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div 
            className="max-w-3xl"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 text-lodiss-red text-xs font-semibold uppercase tracking-widest mb-4">
              <span className="w-8 h-px bg-lodiss-red inline-block"></span>
              {lang === 'fr' ? 'Distributeur B2B certifié' : 'Certified B2B distributor'}
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-950 leading-[1.1] mb-6">
              {T.hero.title}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-gray-500 mb-10 leading-relaxed max-w-2xl">
              {T.hero.subtitle}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setPage('catalogue')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150 text-base"
              >
                {T.hero.cta1}
                <ChevronRight size={18} />
              </button>
              <button
                onClick={() => setPage('reseller')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-gray-900 text-gray-900 hover:bg-gray-950 hover:text-white font-semibold rounded-lg transition-colors duration-150 text-base"
              >
                {T.hero.cta2}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-4 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {trustItems.map((item, i) => (
              <motion.div key={i} variants={fadeInUp} className="flex items-center gap-3 py-2">
                <span className="text-lodiss-red flex-shrink-0">{item.icon}</span>
                <span className="text-sm text-gray-300 font-medium">{item.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Univers */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="mb-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-950">{T.universes.title}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {universeCards.map((card, i) => (
              <motion.div
                key={card.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }
                }}
                className="group border border-gray-100 rounded-xl overflow-hidden hover:shadow-md hover:border-gray-200 transition-all duration-200 bg-white"
              >
                <div className="aspect-[16/9] overflow-hidden bg-gray-50">
                  <img
                    src={card.image}
                    alt={card.data.name}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-950 mb-2">{card.data.name}</h3>
                  <p className="text-sm text-gray-500 mb-4 leading-relaxed">{card.data.desc}</p>
                  <ul className="space-y-1 mb-5">
                    {card.data.sub.map((s, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check size={12} className="text-lodiss-red flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => { setCatalogueFilter(card.filter); setPage('catalogue'); }}
                    className="flex items-center gap-1.5 text-sm font-semibold text-lodiss-red hover:text-lodiss-dark transition-colors duration-150"
                  >
                    {T.universes.explore}
                    <ChevronRight size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to buy */}
      <section className="bg-gray-50 py-16 lg:py-20 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-950 mb-10">{T.howToBuy.title}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {/* Direct purchase */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-lodiss-red text-white flex items-center justify-center flex-shrink-0">
                  <ShoppingCartIcon />
                </div>
                <h3 className="font-bold text-gray-950 text-lg">{T.howToBuy.direct.title}</h3>
              </div>
              <ol className="space-y-3">
                {T.howToBuy.direct.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Volume quote */}
            <div className="bg-white rounded-xl border border-amber-200 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <FileIcon />
                </div>
                <h3 className="font-bold text-gray-950 text-lg">{T.howToBuy.quote.title}</h3>
              </div>
              <ol className="space-y-3 mb-4">
                {T.howToBuy.quote.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-50 text-amber-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700">{step}</span>
                  </li>
                ))}
              </ol>
              <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">{T.howToBuy.quote.note}</p>
            </div>
          </div>

          {/* Threshold table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h4 className="font-semibold text-gray-900 text-sm">{T.howToBuy.thresholds.title}</h4>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{T.howToBuy.thresholds.cat}</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{T.howToBuy.thresholds.threshold}</th>
                </tr>
              </thead>
              <tbody>
                {thresholdRows.map((row, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-6 py-3 text-gray-700 font-medium">{row.cat}</td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                        {row.val}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="flex items-end justify-between mb-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-950">{T.bestsellers.title}</h2>
              <p className="text-sm text-gray-500 mt-1">{T.bestsellers.subtitle}</p>
            </div>
            <button
              onClick={() => setPage('catalogue')}
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-lodiss-red hover:text-lodiss-dark"
            >
              {lang === 'fr' ? 'Tout voir' : 'View all'}
              <ChevronRight size={14} />
            </button>
          </motion.div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[1,2,3,4].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {bestsellers.map(p => (
                <motion.div key={p.id} variants={fadeInUp}>
                  <ProductCard product={p} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Reseller CTA */}
      <section className="bg-gray-950 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-lodiss-red text-xs font-semibold uppercase tracking-widest mb-3 block">
                {T.reseller.title}
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {T.reseller.subtitle}
              </h2>
              <ul className="space-y-3 mb-8">
                {T.reseller.advantages.map((adv, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={14} className="text-lodiss-red mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-white text-sm font-semibold">{adv.title}</span>
                      <span className="text-gray-400 text-sm"> — {adv.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPage('reseller')}
                className="flex items-center gap-2 px-6 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150"
              >
                {T.reseller.cta}
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
                {lang === 'fr' ? 'Comment ça marche' : 'How it works'}
              </h4>
              <ol className="space-y-4">
                {T.reseller.steps.map((step, i) => (
                  <li key={i} className="flex items-center gap-4">
                    <span className="w-8 h-8 rounded-full bg-lodiss-red text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-gray-300 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ShoppingCartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
      <path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 01-8 0"/>
    </svg>
  );
}

function FileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}
